import FAQItems from "../components/FAQItems";

const TEXT = "#EDEDED";
const TEXT_DIM = "#a0a0a0";
const TEAL = "#4FCDC4";
const TEAL_DIM = "rgba(79,205,196,0.15)";
const CARD_BORDER = "rgba(255,255,255,0.04)";
const RED = "#f87171";
const RED_DIM = "rgba(248,113,113,0.06)";
const RED_BORDER = "rgba(248,113,113,0.35)";
const AMBER = "#fbbf24";
const PURPLE = "#a78bfa";

function H2({ id, children }) {
  return <h2 id={id} style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: "clamp(22px, 3vw, 28px)", color: TEXT, margin: "48px 0 16px", letterSpacing: "-0.01em", lineHeight: 1.2 }}>{children}</h2>;
}
function H3({ children }) {
  return <h3 style={{ fontFamily: "var(--display)", fontWeight: 700, fontSize: 19, color: TEXT, margin: "32px 0 12px", letterSpacing: "-0.005em", lineHeight: 1.3 }}>{children}</h3>;
}
function P({ children }) {
  return <p style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.85, margin: "0 0 18px" }}>{children}</p>;
}
function Li({ children }) {
  return <li style={{ fontFamily: "var(--body)", fontSize: 16, color: TEXT_DIM, lineHeight: 1.8, marginBottom: 8 }}>{children}</li>;
}
function Strong({ children }) {
  return <strong style={{ color: TEXT, fontWeight: 700 }}>{children}</strong>;
}
function A({ href, children, external }) {
  if (external) {
    return <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}>{children}</a>;
  }
  return <a href={href} style={{ color: TEAL, textDecoration: "underline", fontWeight: 600 }}>{children}</a>;
}
function PullQuote({ children }) {
  return (
    <blockquote style={{ margin: "32px 0", padding: "20px 24px", borderLeft: `3px solid ${TEAL}`, fontFamily: "var(--display)", fontSize: 20, fontWeight: 600, color: TEXT, lineHeight: 1.4, fontStyle: "normal" }}>
      {children}
    </blockquote>
  );
}
function Code({ children }) {
  return <code style={{ fontFamily: "var(--mono)", fontSize: 14, color: TEXT, background: "rgba(255,255,255,0.06)", padding: "2px 6px", borderRadius: 4 }}>{children}</code>;
}

/* ───────────────────────────────────────────────────────────────────── */
/* DATA CONSTANTS                                                        */
/* ───────────────────────────────────────────────────────────────────── */

const HINT_METHOD_STEPS = [
  {
    name: "Source from sales",
    text: "Spend 60 minutes with a closer. Read the last 20 closed-won notes. List the 8 buyer scenarios that produced revenue last quarter.",
  },
  {
    name: "Pick the pattern",
    text: "Classify each scenario as one of the 5 Context Hint Patterns: Persona + Intent, Question, Topic + Disqualifier, Outcome, or Stack Comparison.",
  },
  {
    name: "Write the 4 components",
    text: "Compose persona, intent, scope, and (where adjacent intents drain spend) a disqualifier. Target 1-2 sentences, under 280 characters.",
  },
  {
    name: "Score against the rubric",
    text: "Apply the Hint-Quality Scorecard. Score persona, intent, scope, disqualifier, and length 0-2 each. Aim for 8 or higher out of 10 before launching.",
  },
  {
    name: "Launch one hint per ad group",
    text: "One focused hint per ad group, never stacked hints. Run two weeks. Read scenario engagement, not keyword density. Iterate the language between cohorts.",
  },
];

const REWRITES = [
  {
    keyword: "best CRM for sales teams",
    hint: "Marketing leaders comparing CRM platforms for a 50-person team, not implementation help",
    pattern: "Persona + Intent",
    why: "Names persona, intent, and scope. The disqualifier names implementation traffic as out of scope.",
  },
  {
    keyword: "HubSpot alternatives",
    hint: "HubSpot users considering a switch to a more outbound-focused CRM",
    pattern: "Stack Comparison",
    why: "Anchors on a known-tool migration. Reaches users mid-evaluation, not mid-implementation.",
  },
  {
    keyword: "how to shorten sales cycle",
    hint: "B2B sales teams trying to cut the sales cycle from 90 days to 60 days",
    pattern: "Outcome",
    why: "Bypasses the category-naming layer. Reaches users by goal, even when they don't name a product type.",
  },
  {
    keyword: "sales pipeline software",
    hint: "Founders setting up a first sales pipeline in their first 30 hires",
    pattern: "Persona + Intent",
    why: "Names the company maturity stage. Names enterprise replatform conversations as out of scope.",
  },
  {
    keyword: "what is a sales CRM",
    hint: "Users asking what a sales CRM does and whether they need one yet",
    pattern: "Question",
    why: "Mirrors the WH-question shape. Reaches first-touch buyers; lower CVR than BOFU but qualifies new audiences.",
  },
  {
    keyword: "Pipedrive vs HubSpot",
    hint: "Pipedrive users evaluating whether HubSpot's marketing add-on justifies switching CRMs",
    pattern: "Stack Comparison",
    why: "Targets the specific switching friction (the marketing add-on). Does not describe brand-only Pipedrive searches.",
  },
  {
    keyword: "sales team productivity tools",
    hint: "RevOps leaders shortlisting tools that consolidate dialer, CRM, and email outreach into one workflow",
    pattern: "Persona + Intent",
    why: "Specifies consolidation intent and the tool mix. Leaves single-feature shoppers out of the description.",
  },
  {
    keyword: "CRM with built in dialer",
    hint: "B2B teams replacing a stack of dialer plus CRM plus outreach with one tool",
    pattern: "Outcome",
    why: "Outcome-led. Reaches users who haven't decided on a category-named approach yet.",
  },
];

const SCORECARD_CRITERIA = [
  {
    name: "Persona",
    zero: "No persona named",
    one: "Generic role label ('sales teams')",
    two: "Specific role + qualifier ('RevOps leaders at 50-person teams')",
  },
  {
    name: "Intent",
    zero: "No verb (or implied only)",
    one: "Generic action ('looking for', 'interested in')",
    two: "Specific decision verb ('comparing', 'evaluating', 'switching')",
  },
  {
    name: "Scope",
    zero: "No qualifying context",
    one: "Single dimension (company size OR vertical)",
    two: "Two or more dimensions (role + company size + stage)",
  },
  {
    name: "Disqualifier",
    zero: "None",
    one: "Implicit exclusion (no 'not X' phrase)",
    two: "Explicit 'not X' disqualifier",
  },
  {
    name: "Length",
    zero: "Over 280 chars or fragment",
    one: "1-2 sentences but 250-280 chars (drift risk)",
    two: "1-2 sentences, under 200 chars",
  },
];

const WORKED_EXAMPLES = [
  {
    label: "Ready to launch",
    score: 9,
    hint: "Marketing leaders comparing CRM platforms for a 50-person team, not implementation help",
    breakdown: [
      { name: "Persona", points: "1" },
      { name: "Intent", points: "2" },
      { name: "Scope", points: "2" },
      { name: "Disqualifier", points: "2" },
      { name: "Length", points: "2" },
    ],
  },
  {
    label: "Revise",
    score: 5,
    hint: "Sales teams comparing CRM platforms",
    breakdown: [
      { name: "Persona", points: "1" },
      { name: "Intent", points: "2" },
      { name: "Scope", points: "0" },
      { name: "Disqualifier", points: "0" },
      { name: "Length", points: "2" },
    ],
  },
];

/* ───────────────────────────────────────────────────────────────────── */
/* VISUAL 1: The 5-Step Hint Method                                       */
/* ───────────────────────────────────────────────────────────────────── */

function HintMethodDiagram() {
  return (
    <div style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>The 5-Step Hint Method</div>
      <div style={{ fontSize: 10, color: TEXT_DIM, marginBottom: 24, fontFamily: "var(--body)" }}>Source the scenario, pick the pattern, write the components, score, launch.</div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {HINT_METHOD_STEPS.map((step, i) => (
          <div key={i} style={{
            display: "grid",
            gridTemplateColumns: "60px 1fr",
            gap: 16,
            padding: "16px 18px",
            background: "rgba(255,255,255,0.03)",
            borderRadius: 8,
            border: `1px solid ${CARD_BORDER}`,
            alignItems: "start",
          }}>
            <div style={{
              width: 44, height: 44, borderRadius: "50%",
              background: TEAL_DIM,
              border: `1px solid rgba(79,205,196,0.35)`,
              display: "flex", alignItems: "center", justifyContent: "center",
              color: TEAL, fontFamily: "var(--mono)", fontSize: 16, fontWeight: 700,
            }}>{String(i + 1).padStart(2, "0")}</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: TEXT, marginBottom: 6, lineHeight: 1.3, fontFamily: "var(--body)" }}>{step.name}</div>
              <div style={{ fontSize: 13, color: TEXT_DIM, lineHeight: 1.55, fontFamily: "var(--body)" }}>{step.text}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────── */
/* VISUAL 2: The Before/After Rewrite Gallery                            */
/* ───────────────────────────────────────────────────────────────────── */

function RewriteGallery() {
  return (
    <div className="blog-scroll-wrap" style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px", overflowX: "auto" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>The Before/After Rewrite Gallery</div>
      <div style={{ fontSize: 10, color: TEXT_DIM, marginBottom: 20, fontFamily: "var(--body)" }}>8 keyword-to-hint translations across all 5 patterns.</div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 720 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${CARD_BORDER}` }}>
            <th style={{ textAlign: "left", padding: "10px 12px", color: TEAL, fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, width: "22%" }}>Original keyword</th>
            <th style={{ textAlign: "left", padding: "10px 12px", color: TEAL, fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, width: "32%" }}>The 4-part hint</th>
            <th style={{ textAlign: "left", padding: "10px 12px", color: TEAL, fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, width: "16%" }}>Pattern</th>
            <th style={{ textAlign: "left", padding: "10px 12px", color: TEAL, fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, width: "30%" }}>Why it works</th>
          </tr>
        </thead>
        <tbody>
          {REWRITES.map((r, i) => (
            <tr key={i} style={{ borderBottom: `1px solid ${CARD_BORDER}` }}>
              <td style={{ padding: "12px 12px", color: TEXT_DIM, fontFamily: "var(--mono)", fontSize: 12, verticalAlign: "top", lineHeight: 1.4 }}>"{r.keyword}"</td>
              <td style={{ padding: "12px 12px", color: TEXT, verticalAlign: "top", lineHeight: 1.55, fontStyle: "italic", fontFamily: "var(--body)" }}>"{r.hint}"</td>
              <td style={{ padding: "12px 12px", color: TEAL, fontWeight: 600, verticalAlign: "top", lineHeight: 1.4, fontSize: 12, fontFamily: "var(--body)" }}>{r.pattern}</td>
              <td style={{ padding: "12px 12px", color: TEXT_DIM, verticalAlign: "top", lineHeight: 1.55, fontFamily: "var(--body)" }}>{r.why}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────── */
/* VISUAL 3: The Hint-Quality Scorecard                                   */
/* ───────────────────────────────────────────────────────────────────── */

function ScorecardCard() {
  return (
    <div className="blog-scroll-wrap" style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px", overflowX: "auto" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>The Hint-Quality Scorecard</div>
      <div style={{ fontSize: 10, color: TEXT_DIM, marginBottom: 20, fontFamily: "var(--body)" }}>5 questions, 0-2 points each, 10 total. Aim for 8 or higher before launch.</div>

      <div style={{ minWidth: 600 }}>
        {/* Rubric table */}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 28 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${CARD_BORDER}` }}>
              <th style={{ textAlign: "left", padding: "10px 12px", color: TEAL, fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, width: "22%" }}>Criterion</th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: TEAL, fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, width: "26%" }}>0 pts</th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: TEAL, fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, width: "26%" }}>1 pt</th>
              <th style={{ textAlign: "left", padding: "10px 12px", color: TEAL, fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, width: "26%" }}>2 pts</th>
            </tr>
          </thead>
          <tbody>
            {SCORECARD_CRITERIA.map((c, i) => (
              <tr key={i} style={{ borderBottom: `1px solid ${CARD_BORDER}` }}>
                <td style={{ padding: "12px 12px", color: TEXT, fontWeight: 700, verticalAlign: "top", lineHeight: 1.4, fontFamily: "var(--body)" }}>{c.name}</td>
                <td style={{ padding: "12px 12px", color: TEXT_DIM, verticalAlign: "top", lineHeight: 1.5, fontSize: 12, fontFamily: "var(--body)" }}>{c.zero}</td>
                <td style={{ padding: "12px 12px", color: TEXT_DIM, verticalAlign: "top", lineHeight: 1.5, fontSize: 12, fontFamily: "var(--body)" }}>{c.one}</td>
                <td style={{ padding: "12px 12px", color: TEXT_DIM, verticalAlign: "top", lineHeight: 1.5, fontSize: 12, fontFamily: "var(--body)" }}>{c.two}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Worked examples */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
          {WORKED_EXAMPLES.map((ex, i) => {
            const isPass = ex.score >= 8;
            const accentColor = isPass ? TEAL : AMBER;
            const accentBg = isPass ? TEAL_DIM : "rgba(251,191,36,0.08)";
            const accentBorder = isPass ? "rgba(79,205,196,0.35)" : "rgba(251,191,36,0.35)";
            return (
              <div key={i} style={{
                background: accentBg,
                border: `1px solid ${accentBorder}`,
                borderRadius: 8, padding: "16px 18px",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
                  <div style={{ fontSize: 10, fontFamily: "var(--mono)", color: accentColor, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700 }}>
                    {ex.label}
                  </div>
                  <div style={{ fontSize: 18, fontFamily: "var(--mono)", color: accentColor, fontWeight: 700 }}>
                    {ex.score}/10
                  </div>
                </div>
                <div style={{ fontSize: 13, color: TEXT, fontStyle: "italic", marginBottom: 12, lineHeight: 1.55, fontFamily: "var(--body)" }}>
                  "{ex.hint}"
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, fontSize: 10, color: TEXT_DIM, fontFamily: "var(--mono)", lineHeight: 1.5 }}>
                  {ex.breakdown.map((b, j) => (
                    <span key={j} style={{ background: "rgba(255,255,255,0.04)", padding: "2px 6px", borderRadius: 3 }}>
                      {b.name} {b.points}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────────── */
/* DATA + VISUALS: 10 WORKED EXAMPLES (ported from best-chatgpt-ads-      */
/* context-hint-examples)                                                 */
/* ───────────────────────────────────────────────────────────────────── */

const HINTS = [
  {
    n: 1,
    pattern: "Persona + Intent",
    vertical: "B2B SaaS / RevOps",
    hint: "RevOps leaders at Series B SaaS companies comparing multi-touch attribution platforms, not single-touch tracking",
    components: [
      { name: "Persona", points: 2 },
      { name: "Intent", points: 2 },
      { name: "Scope", points: 2 },
      { name: "Disqualifier", points: 2 },
      { name: "Length", points: 2 },
    ],
    score: 10,
    chars: 110,
    provenance: "Sales call language",
    why: "Names a specific role at a specific company stage, the comparison verb, and names single-touch tracking conversations (UTM-only setups the campaign cannot convert) as out of scope.",
  },
  {
    n: 2,
    pattern: "Persona + Intent",
    vertical: "DevTools / Engineering",
    hint: "Senior platform engineers evaluating observability tools to cut weekly on-call burden by 30 minutes",
    components: [
      { name: "Persona", points: 2 },
      { name: "Intent", points: 2 },
      { name: "Scope", points: 2 },
      { name: "Disqualifier", points: 0 },
      { name: "Length", points: 2 },
    ],
    score: 8,
    chars: 98,
    provenance: "Reddit r/devops thread archetype",
    why: "Names a specific engineer role plus a measurable outcome (30 minutes per week). Disqualifier is omitted because the persona is narrow enough that adjacent intents do not dilute the cohort. The 8 of 10 score still clears the launch gate.",
  },
  {
    n: 3,
    pattern: "Question",
    vertical: "Marketing Ops",
    hint: "Marketing operators asking how to migrate HubSpot data to Salesforce without losing 6 months of campaign history",
    components: [
      { name: "Persona", points: 2 },
      { name: "Intent", points: 2 },
      { name: "Scope", points: 2 },
      { name: "Disqualifier", points: 0 },
      { name: "Length", points: 2 },
    ],
    score: 8,
    chars: 113,
    provenance: "Customer support transcript",
    why: "Mirrors the WH-question shape (how do I migrate). Names the tool-to-tool migration plus a specific data scope (6 months of campaign history). Reaches conversations where the user is actively asking, not researching.",
  },
  {
    n: 4,
    pattern: "Question",
    vertical: "Data engineering",
    hint: "Data engineers asking what is the difference between data observability and data monitoring for production pipelines",
    components: [
      { name: "Persona", points: 2 },
      { name: "Intent", points: 2 },
      { name: "Scope", points: 2 },
      { name: "Disqualifier", points: 0 },
      { name: "Length", points: 2 },
    ],
    score: 8,
    chars: 117,
    provenance: "Real comparison-search archetype",
    why: "Definitional question pattern. The production-pipelines scope leaves home-lab and personal-project conversations out of the description, which is where most of the noise sits in this category.",
  },
  {
    n: 5,
    pattern: "Topic + Disqualifier",
    vertical: "B2B SaaS martech",
    hint: "B2B SaaS marketing teams comparing martech stacks for Series B rollouts, not implementation help",
    components: [
      { name: "Persona", points: 2 },
      { name: "Intent", points: 2 },
      { name: "Scope", points: 2 },
      { name: "Disqualifier", points: 2 },
      { name: "Length", points: 2 },
    ],
    score: 10,
    chars: 96,
    provenance: "Sales call language",
    why: "Names persona, comparison verb, and Series B rollout scope, then explicitly excludes implementation help. Implementation traffic is the dominant adjacent intent in martech and the disqualifier matters because evaluation budget cannot afford it.",
  },
  {
    n: 6,
    pattern: "Topic + Disqualifier",
    vertical: "DevTools / Cloud-native",
    hint: "Engineering managers evaluating CI/CD platforms for cloud-native apps, not on-prem deployment scenarios",
    components: [
      { name: "Persona", points: 2 },
      { name: "Intent", points: 2 },
      { name: "Scope", points: 2 },
      { name: "Disqualifier", points: 2 },
      { name: "Length", points: 2 },
    ],
    score: 10,
    chars: 105,
    provenance: "Reddit r/devops thread archetype",
    why: "Cloud-native and on-prem buyers have different toolchain requirements. The disqualifier names on-prem conversations, which the cloud-native vendor cannot serve, as out of scope; they are a dense adjacent intent in this category.",
  },
  {
    n: 7,
    pattern: "Outcome",
    vertical: "B2B Sales",
    hint: "B2B sales operations leaders trying to cut mid-market sales cycle from 90 days to 45, not enterprise deals",
    components: [
      { name: "Persona", points: 2 },
      { name: "Intent", points: 2 },
      { name: "Scope", points: 2 },
      { name: "Disqualifier", points: 2 },
      { name: "Length", points: 2 },
    ],
    score: 10,
    chars: 109,
    provenance: "Real RevOps OKR language",
    why: "Outcome-led, bypasses the category layer entirely. The user does not have to know they need a sales-acceleration tool to land on the cohort. The disqualifier names enterprise sales-cycle conversations, which would never convert for a mid-market vendor, as out of scope.",
  },
  {
    n: 8,
    pattern: "Outcome",
    vertical: "Martech consolidation",
    hint: "Marketing leaders trying to consolidate 3 disconnected martech tools into 1 platform that syncs with HubSpot",
    components: [
      { name: "Persona", points: 2 },
      { name: "Intent", points: 2 },
      { name: "Scope", points: 2 },
      { name: "Disqualifier", points: 0 },
      { name: "Length", points: 2 },
    ],
    score: 8,
    chars: 110,
    provenance: "Reddit r/marketing pain-point archetype",
    why: "Concrete outcome (3 to 1) plus an integration anchor (HubSpot). Reaches stack-consolidation conversations where the buyer is goal-oriented but has not yet shortlisted vendors.",
  },
  {
    n: 9,
    pattern: "Stack Comparison",
    vertical: "Marketing automation migration",
    hint: "Marketing operators migrating from Marketo to HubSpot for sub-100-employee SaaS, not enterprise migrations",
    components: [
      { name: "Persona", points: 2 },
      { name: "Intent", points: 2 },
      { name: "Scope", points: 2 },
      { name: "Disqualifier", points: 2 },
      { name: "Length", points: 2 },
    ],
    score: 10,
    chars: 108,
    provenance: "Customer support transcript",
    why: "Named-tool migration with a company-size disqualifier. Enterprise migrations need different toolchain consideration, and the disqualifier names those conversations as out of scope for the cohort.",
  },
  {
    n: 10,
    pattern: "Stack Comparison",
    vertical: "Observability cost migration",
    hint: "Engineering leads comparing Datadog vs New Relic for cloud-native apps with monthly Datadog bills over $50K",
    components: [
      { name: "Persona", points: 2 },
      { name: "Intent", points: 2 },
      { name: "Scope", points: 2 },
      { name: "Disqualifier", points: 0 },
      { name: "Length", points: 2 },
    ],
    score: 8,
    chars: 111,
    provenance: "Sales call language",
    why: "Names the budget pain ($50K monthly Datadog spend) as the qualifying scope. Reaches cost-driven comparison conversations where the buyer has a specific switching trigger rather than a general curiosity.",
  },
];

const PATTERN_COLOR = {
  "Persona + Intent": PURPLE,
  "Question": TEAL,
  "Topic + Disqualifier": RED,
  "Outcome": AMBER,
  "Stack Comparison": "#60a5fa",
};

const PATTERN_BG = {
  "Persona + Intent": "rgba(167,139,250,0.12)",
  "Question": "rgba(79,205,196,0.12)",
  "Topic + Disqualifier": "rgba(248,113,113,0.10)",
  "Outcome": "rgba(251,191,36,0.10)",
  "Stack Comparison": "rgba(96,165,250,0.12)",
};

const PATTERN_BORDER = {
  "Persona + Intent": "rgba(167,139,250,0.30)",
  "Question": "rgba(79,205,196,0.30)",
  "Topic + Disqualifier": "rgba(248,113,113,0.30)",
  "Outcome": "rgba(251,191,36,0.30)",
  "Stack Comparison": "rgba(96,165,250,0.30)",
};

function HintExamplesTable() {
  return (
    <div className="blog-scroll-wrap" style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px", overflowX: "auto" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>The 10 hints at a glance</div>
      <div style={{ fontSize: 10, color: TEXT_DIM, marginBottom: 20, fontFamily: "var(--body)" }}>2 examples per pattern, all sourced from real B2B conversations.</div>

      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 720 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${CARD_BORDER}` }}>
            <th style={{ textAlign: "left", padding: "10px 12px", color: TEAL, fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, width: "5%" }}>#</th>
            <th style={{ textAlign: "left", padding: "10px 12px", color: TEAL, fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, width: "20%" }}>Pattern</th>
            <th style={{ textAlign: "left", padding: "10px 12px", color: TEAL, fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, width: "20%" }}>Vertical</th>
            <th style={{ textAlign: "left", padding: "10px 12px", color: TEAL, fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, width: "47%" }}>Hint preview</th>
            <th style={{ textAlign: "left", padding: "10px 12px", color: TEAL, fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, width: "8%" }}>Score</th>
          </tr>
        </thead>
        <tbody>
          {HINTS.map((h) => (
            <tr key={h.n} style={{ borderBottom: `1px solid ${CARD_BORDER}` }}>
              <td style={{ padding: "12px 12px", color: TEXT_DIM, fontFamily: "var(--mono)", fontSize: 12, verticalAlign: "top", lineHeight: 1.4 }}>{String(h.n).padStart(2, "0")}</td>
              <td style={{ padding: "12px 12px", color: PATTERN_COLOR[h.pattern], fontWeight: 600, verticalAlign: "top", lineHeight: 1.4, fontSize: 12, fontFamily: "var(--body)" }}>{h.pattern}</td>
              <td style={{ padding: "12px 12px", color: TEXT_DIM, verticalAlign: "top", lineHeight: 1.4, fontSize: 12, fontFamily: "var(--body)" }}>{h.vertical}</td>
              <td style={{ padding: "12px 12px", color: TEXT, verticalAlign: "top", lineHeight: 1.5, fontStyle: "italic", fontSize: 12, fontFamily: "var(--body)" }}>"{h.hint.length > 80 ? h.hint.slice(0, 78) + "…" : h.hint}"</td>
              <td style={{ padding: "12px 12px", color: h.score === 10 ? TEAL : AMBER, verticalAlign: "top", fontFamily: "var(--mono)", fontSize: 13, fontWeight: 700 }}>{h.score}/10</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderHintBlock(h) {
  const patternColor = PATTERN_COLOR[h.pattern];
  const patternBg = PATTERN_BG[h.pattern];
  const patternBorder = PATTERN_BORDER[h.pattern];
  const isPerfect = h.score === 10;
  return (
    <div id={`hint-${h.n}`} key={h.n} style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: "22px 24px", margin: "20px 0 28px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: 14 }}>
        <span style={{ fontSize: 10, fontFamily: "var(--mono)", color: patternColor, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, background: patternBg, border: `1px solid ${patternBorder}`, padding: "3px 8px", borderRadius: 4 }}>
          Hint #{h.n} · {h.pattern}
        </span>
        <span style={{ fontSize: 10, fontFamily: "var(--mono)", color: TEXT_DIM, letterSpacing: "0.04em" }}>{h.vertical}</span>
        <span style={{ marginLeft: "auto", fontSize: 14, fontFamily: "var(--mono)", color: isPerfect ? TEAL : AMBER, fontWeight: 700 }}>
          {h.score}/10
        </span>
      </div>

      <div style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${CARD_BORDER}`, borderRadius: 8, padding: "16px 18px", fontFamily: "var(--body)", fontSize: 15, fontStyle: "italic", color: TEXT, lineHeight: 1.55, marginBottom: 14 }}>
        "{h.hint}"
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
        {h.components.map((c) => (
          <span key={c.name} style={{ fontSize: 10, fontFamily: "var(--mono)", color: c.points === 2 ? TEAL : (c.points === 1 ? AMBER : TEXT_DIM), background: "rgba(255,255,255,0.04)", padding: "3px 7px", borderRadius: 3, letterSpacing: "0.02em" }}>
            {c.name} {c.points}
          </span>
        ))}
        <span style={{ fontSize: 10, fontFamily: "var(--mono)", color: TEXT_DIM, background: "rgba(255,255,255,0.04)", padding: "3px 7px", borderRadius: 3, letterSpacing: "0.02em", marginLeft: "auto" }}>
          {h.chars} chars
        </span>
      </div>

      <div style={{ fontSize: 14, color: TEXT_DIM, lineHeight: 1.65, fontFamily: "var(--body)", marginBottom: 10 }}>
        <span style={{ color: TEXT, fontWeight: 600 }}>Why it works.</span> {h.why}
      </div>

      <div style={{ fontSize: 11, color: TEXT_DIM, fontFamily: "var(--mono)", letterSpacing: "0.04em" }}>
        Source: {h.provenance}
      </div>
    </div>
  );
}

function HintQualityFrequency() {
  const criteria = ["Persona", "Intent", "Scope", "Disqualifier", "Length"];
  const stats = criteria.map((name) => {
    const total = HINTS.length;
    const fullPoints = HINTS.filter((h) => h.components.find((c) => c.name === name)?.points === 2).length;
    return { name, fullPoints, total, pct: Math.round((fullPoints / total) * 100) };
  });

  return (
    <div className="blog-scroll-wrap" style={{ background: "#0f0f11", borderRadius: 12, border: `1px solid ${CARD_BORDER}`, padding: 24, margin: "16px 0 32px", overflowX: "auto" }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT, marginBottom: 4, fontFamily: "var(--body)" }}>What "good" has in common across the 10 hints</div>
      <div style={{ fontSize: 10, color: TEXT_DIM, marginBottom: 24, fontFamily: "var(--body)" }}>% of the 10 hints that scored 2 of 2 on each scorecard criterion.</div>

      <div style={{ minWidth: 480 }}>
        {stats.map((s) => (
          <div key={s.name} style={{ display: "grid", gridTemplateColumns: "140px 1fr 90px", gap: 16, alignItems: "center", padding: "10px 0", borderTop: `1px solid ${CARD_BORDER}` }}>
            <div style={{ color: TEXT, fontSize: 13, fontFamily: "var(--body)", fontWeight: 600 }}>{s.name}</div>
            <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 4, height: 18, position: "relative", overflow: "hidden" }}>
              <div style={{ background: s.pct === 100 ? TEAL : (s.pct >= 50 ? AMBER : RED), height: "100%", width: `${s.pct}%`, borderRadius: 4 }}></div>
            </div>
            <div style={{ color: s.pct === 100 ? TEAL : (s.pct >= 50 ? AMBER : RED), fontFamily: "var(--mono)", fontSize: 13, fontWeight: 700, textAlign: "right" }}>{s.pct}%</div>
          </div>
        ))}
        <div style={{ marginTop: 20, padding: "14px 16px", background: "rgba(255,255,255,0.03)", border: `1px solid ${CARD_BORDER}`, borderRadius: 8, fontSize: 13, color: TEXT_DIM, lineHeight: 1.6, fontFamily: "var(--body)" }}>
          <Strong>Average score across 10 hints: 9.0 of 10.</Strong> The scope, persona, intent, and length criteria are universal. The disqualifier is the discretionary component, present in 5 of 10 hints where adjacent intent density warranted it. That 50/50 split tracks the launch-gate finding: a disqualifier is high-leverage but not mandatory.
        </div>
      </div>
    </div>
  );
}

const examplesItemListSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "10 Best ChatGPT Ads Context Hint Examples for B2B (2026)",
  "description": "Ten worked context hint examples for B2B ChatGPT Ads campaigns, scored against the Hint-Quality Scorecard and mapped to the 5-Pattern taxonomy.",
  "itemListOrder": "https://schema.org/ItemListUnordered",
  "numberOfItems": HINTS.length,
  "itemListElement": HINTS.map((h) => ({
    "@type": "ListItem",
    "position": h.n,
    "name": `Hint #${h.n}: ${h.pattern} for ${h.vertical}`,
    "url": `https://www.ai-advisors.ai/blog/how-to-write-effective-context-hints#hint-${h.n}`,
    "description": h.hint,
  })),
};

/* ───────────────────────────────────────────────────────────────────── */
/* FAQs (populated in Section D)                                         */
/* ───────────────────────────────────────────────────────────────────── */

const faqs = [
  {
    q: "How do I choose between the 5 Context Hint Patterns when a scenario fits more than one?",
    a: "Most scenarios fit one pattern best when you ask 'what would the buyer's most representative ChatGPT prompt sound like?' Persona + Intent fits comparison; Question fits how-to and what-is; Stack Comparison fits named-tool migration; Outcome fits goal-language; Topic + Disqualifier fits scenarios where adjacent traffic is the bigger problem. If two patterns fit, write both as ad groups and let the matcher decide. Our free converter outputs the matching hint for any keyword.",
  },
  {
    q: "Should every component of the 4-part hint be present in every hint, or just the relevant ones?",
    a: "Persona, intent, and scope should be present in every hint. The disqualifier is optional and only earns its place when adjacent intents would dilute spend. Many disciplined B2B hints include a disqualifier, but plenty of working hints don't need one if the other components are tight. The scorecard captures this. A missing disqualifier scores 0 but doesn't fail the launch gate by itself if the other four components score 8 or higher.",
  },
  {
    q: "What's the right way to test whether my hint is too narrow versus too broad?",
    a: "Run the hint for two weeks and read the impression volume. Very low counts (under a few hundred per week) suggest the hint is too narrow; the matcher can't find aligned conversations. Very high volume with poor click-through suggests the hint is too broad; too many adjacent scenarios qualify. The actionable rule: if the matcher serves the placement against a clearly-irrelevant conversation in spot-checking, the hint is too broad.",
  },
  {
    q: "Can I use the same context hint for a Views campaign and a Clicks campaign?",
    a: "Yes. The hint is platform-level targeting that applies across campaign objectives. It determines which conversations qualify for placement; the campaign objective determines what the matcher optimizes for once eligibility is established. Most disciplined B2B teams test the same hint across both objectives in the first 30 days. Views (shown as Reach in Ads Manager) measures audience size, Clicks measures intent quality, and the comparison surfaces whether the hint is broader or narrower than expected.",
  },
  {
    q: "How many ad groups should a B2B campaign have in the first 30 days?",
    a: "Three to five ad groups, each with one focused hint. More than five splinters the budget too thin to read signal per cohort; fewer than three doesn't explore enough of the buyer scenario space. The 5-Step Hint Method's Step 1 produces 8 buyer scenarios. Pick the 3-5 highest-confidence scenarios for the pilot, hold the rest in reserve for the second cohort, and run two-week tests per scenario.",
  },
  {
    q: "Does the 5-Step Hint Method work for B2C as well as B2B?",
    a: "Yes, with persona and scope components doing more lifting than they do in B2B. B2C scenarios tend to have less role-driven personas (consumer rather than RevOps leader) and more lifestyle or moment-based scope ('planning a 2-week trip in October' rather than '50-person team in tech'). The Pattern set and the Scorecard apply identically. The only weak fit is commodity products without a coherent buyer scenario behind them.",
  },
  {
    q: "What happens to my context hints if OpenAI changes the matcher's underlying model?",
    a: "Hints written in the 4-component shape generalize across embedding model changes because the components describe a buyer scenario, not specific tokens. Hints written as keyword strings reformatted into sentences are more sensitive to model updates because they rely on lexical matching that the new model may interpret differently. The disciplined writing approach is more durable than the lazy one. Plan for periodic re-testing every quarter.",
  },
  {
    q: "Can I copy the 10 example hints directly into my campaign?",
    a: "The structural anatomy translates, but the company-size and stack-specific scopes need to match your actual ICP. The 10 worked examples use B2B SaaS, DevTools, and martech persona language because that is what the test corpus is built from. If your buyer is in healthcare, finance, manufacturing, or services, swap the persona and scope language while keeping the 4-component structure (persona, intent, scope, disqualifier) and re-score the result against the rubric before launching.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(({ q, a }) => ({
    "@type": "Question",
    "name": q,
    "acceptedAnswer": { "@type": "Answer", "text": a },
  })),
};

/* ───────────────────────────────────────────────────────────────────── */
/* HowTo + ItemList schemas (computed from HINT_METHOD_STEPS)            */
/* ───────────────────────────────────────────────────────────────────── */

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Write an Effective Context Hint for ChatGPT Ads",
  "description": "The 5-Step Hint Method for writing context hints that match ChatGPT conversations and earn qualified placement at the ad group level.",
  "totalTime": "PT2H",
  "step": HINT_METHOD_STEPS.map((s, i) => ({
    "@type": "HowToStep",
    "position": i + 1,
    "name": s.name,
    "text": s.text,
  })),
};

const itemListSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "The 5-Step Hint Method for ChatGPT Ads",
  "itemListOrder": "https://schema.org/ItemListOrderAscending",
  "numberOfItems": 5,
  "itemListElement": HINT_METHOD_STEPS.map((s, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": s.name,
    "description": s.text,
  })),
};

/* ───────────────────────────────────────────────────────────────────── */
/* CONTENT                                                                */
/* ───────────────────────────────────────────────────────────────────── */

function Content() {
  return (
    <article style={{ maxWidth: 720, margin: "0 auto" }}>

      {/* ─── Quick Answer / Overview ────────────────────────────────── */}
      <div style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${CARD_BORDER}`, borderRadius: 12, padding: "20px 24px", marginBottom: 32 }}>
        <P>
          <Strong>An effective context hint names a buyer at a moment, not a search string.</Strong> The 5-Step Hint Method walks the marketer from sales scenario to launched ad group: source from sales, pick one of the 5 patterns, write the 4 components (persona, intent, scope, disqualifier), score against the rubric, and launch one hint per ad group. This post is the HOW companion to the <A href="/blog/what-are-context-hints-in-chatgpt-ads">5 Patterns reference</A> and its <A href="/blog/what-are-context-hints-in-chatgpt-ads#why-context-hints-matter">5 Strategic Shifts</A>.
        </P>
        <ul style={{ paddingLeft: 20, margin: "8px 0 0" }}>
          <Li><Strong>5-Step Hint Method:</Strong> source from sales, pick the pattern, write 4 components, score, launch one hint per ad group.</Li>
          <Li><Strong>Hint-Quality Scorecard:</Strong> 5 questions, 0-2 points each, 10 total. Aim for 8 or higher before launch.</Li>
          <Li><Strong>8 worked rewrites:</Strong> Google Ads keywords translated into the 4-part hint format with pattern annotation.</Li>
          <Li><Strong>5 hint-killers to avoid:</Strong> skipping sales sourcing, stacking multiple hints, translating negatives separately, retrospective scoring, and iterating mid-cohort.</Li>
          <Li><Strong>Hint research feeds organic AEO:</Strong> the same scenarios feed paid hints AND organic citation strategy.</Li>
        </ul>
      </div>

      {/* ─── §1 ─ WHAT AN EFFECTIVE CONTEXT HINT LOOKS LIKE ───────────── */}
      <H2 id="four-component-anatomy">What an effective context hint looks like</H2>
      <P>
        <Strong>An effective context hint is a 1-to-2-sentence description of a buyer at a moment, with four components packed inside: persona, intent, scope, and (where adjacent traffic drains spend) a disqualifier.</Strong> The platform's matcher reads the sentence as an embedding and aligns it to the embedding of every active <A href="/glossary/chatgpt-ads">ChatGPT Ads</A> conversation. When semantic similarity clears the threshold, the placement serves below the AI's response.
      </P>
      <P>
        Part 1 of this cluster established that every working <A href="/glossary/context-hint">context hint</A> classifies into one of <A href="/blog/what-are-context-hints-in-chatgpt-ads">five canonical patterns</A>. This post zooms in on the writing craft underneath those patterns. The four components are the scaffolding inside any pattern. A hint is "effective" when each component is named with enough specificity that the matcher reads the sentence as describing one buyer scenario, not several adjacent ones.
      </P>

      <H3>The four components</H3>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><Strong>Persona</Strong> names WHO the user is. 'Marketing leaders' is generic; 'RevOps leaders at 50-person teams' is specific. Specificity here separates buyer signal from category noise.</Li>
        <Li><Strong>Intent</Strong> names WHAT the user is trying to do. 'Looking for a CRM' is implied; 'comparing CRM platforms' is a specific decision verb the matcher can align to active comparison conversations.</Li>
        <Li><Strong>Scope</Strong> narrows the qualifying context. 'For a 50-person team' pins one dimension. 'For a 50-person sales team approaching Series B' pins three. Scope is where most "good" hints leave precision on the table.</Li>
        <Li><Strong>Disqualifier</Strong> names who the ad is NOT for. 'Not implementation help' marks adjacent intents the campaign cannot convert as out of scope. It sharpens the description but cannot block placements: OpenAI says hints cannot enforce exclusions. Optional but high-leverage when adjacent intents are dense.</Li>
      </ul>

      <P>
        The cluster's running Blaze CRM example carries all four:
      </P>

      <PullQuote>
        Marketing leaders comparing CRM platforms for a 50-person team, not implementation help
      </PullQuote>

      <P>
        Persona is "Marketing leaders." Intent is "comparing CRM platforms." Scope is "for a 50-person team." Disqualifier is "not implementation help." The hint describes the buyer and names the adjacent intent to avoid, in 87 characters. The sections that follow are the procedure for getting from a list of buyer scenarios to a hint that scores well on all four components.
      </P>

      {/* ─── §2 ─ THE 5-STEP HINT METHOD ─────────────────────────────── */}
      <H2 id="five-step-hint-method">The 5-Step Hint Method</H2>
      <HintMethodDiagram />
      <P>
        <Strong>The 5-Step Hint Method takes a marketer from buyer scenario to launched ad group in five sequential steps:</Strong> source from sales, pick the pattern, write the components, score against the rubric, and launch one hint per ad group. The first cohort of 8 hints takes roughly two hours of writing and scoring time, plus the time it takes to set up creative and conversion tracking.
      </P>

      <H3>Step 1: Source from sales</H3>
      <P>
        <Strong>Estimated time: 60 minutes for 8 scenarios.</Strong> The first artifact is a list of 8 buyer scenarios that produced revenue last quarter, sourced from the people closest to revenue rather than from a keyword tool or website analytics. Spend 60 minutes with a closer or a customer-success rep. Read the last 20 closed-won notes. Each note compresses into three lines: who bought, what they were trying to do, what almost made them not buy. Eight scenarios is enough to seed three to five ad groups in a pilot campaign. If eight is hard to find, the buyer scenarios are not yet sharply defined and the campaign is not ready for launch.
      </P>

      <H3>Step 2: Pick the pattern</H3>
      <P>
        <Strong>Estimated time: 15 minutes for 8 scenarios.</Strong> Each scenario maps to exactly one of the <A href="/blog/what-are-context-hints-in-chatgpt-ads">5 Context Hint Patterns</A>: Persona + Intent, Question, Topic + Disqualifier, Outcome, or Stack Comparison. The match is usually obvious from the closed-won note. A scenario that names a buyer role and a comparison verb is Persona + Intent. A scenario where the buyer's prompt to ChatGPT would start with "what is" or "how do I" is Question. A scenario where the buyer is migrating from a named tool is Stack Comparison. Outcome and Topic + Disqualifier are reserved for scenarios where the buyer does not name a category yet but clearly names a goal or wants to filter out adjacent traffic. Picking the pattern wrong compounds in step 4. The scorecard surfaces it as a low score on at least one component.
      </P>

      <H3>Step 3: Write the 4 components</H3>
      <P>
        <Strong>Estimated time: 30 minutes for 8 hints.</Strong> Take the scenario and the pattern and compose into 1 to 2 sentences: persona, intent, scope, and (where adjacent intents drain spend) a disqualifier. Order them persona, intent, scope, disqualifier by convention; the matcher reads embeddings, not strings, so component order has minimal effect on match precision. Write specifically: 'RevOps leaders at 50-person teams' beats 'sales teams'; 'comparing CRM platforms' beats 'looking for a CRM'; 'for a 50-person team approaching Series B' beats 'for SMBs.' Aim for 280 characters or fewer. Hints longer than that drift across multiple buyer scenarios and the matcher loses signal.
      </P>

      <H3>Step 4: Score against the rubric</H3>
      <P>
        <Strong>Estimated time: 15 minutes per launch batch.</Strong> Apply the <A href="#scorecard">Hint-Quality Scorecard</A> in section 4 below. Five questions, 0-2 points each, 10 total. Aim for 8 or higher before the hint goes live. The scorecard surfaces drift early. A 4-point hint usually has weak scope and no disqualifier, which the matcher translates into low-quality impressions inside conversations the campaign cannot convert. Revising at this stage is cheap. Revising mid-flight, after a campaign has burned through bad placements, is not. Treat the scorecard as a launch gate, not a retrospective tool.
      </P>

      <H3>Step 5: Launch one hint per ad group</H3>
      <P>
        <Strong>Estimated time: 30 minutes initial setup, then ongoing observation.</Strong> One focused hint per ad group, never stacked hints. Multiple ad groups per campaign, each carrying its own hint and its own creative. Run two weeks. Read scenario engagement (qualifying conversation rate, click-to-conversation lift). Keyword density does not apply to embedding-matched targeting. Iterate the hint language between two-week cohorts. Tightening scope or sharpening the disqualifier between cohorts compounds engagement-rate lift across the campaign. The first 30 days of a ChatGPT Ads campaign is signal collection, not steady-state performance.
      </P>

      {/* ─── §3 ─ THE BEFORE/AFTER REWRITE GALLERY ───────────────────── */}
      <H2 id="rewrite-gallery">8 keyword-to-hint rewrites: the before/after gallery</H2>
      <RewriteGallery />
      <P>
        <Strong>The Before/After Rewrite Gallery shows 8 typical Google Ads keywords translated into the 4-part hint format, classified by which of the 5 patterns each one fits.</Strong> The gallery is built to be skimmed: scan for a keyword close to one of yours, read the hint, then borrow the structure for your own scenario.
      </P>
      <P>
        The 8 rows cover the keyword families most B2B paid-search portfolios run: <A href="/glossary/ai-buyer-funnel">BOFU</A> comparison terms ("best CRM for sales teams"), category descriptors ("sales pipeline software"), TOFU informational queries ("what is a sales CRM"), outcome-driven phrases ("how to shorten sales cycle"), and stack-comparison migration searches ("HubSpot alternatives", "Pipedrive vs HubSpot"). Each maps to one of the 5 Patterns. For how often each pattern shows up across 200 B2B SaaS campaigns, see the <A href="/research/google-ads-to-chatgpt-ads-migration-study#patterns">pattern breakdown in our migration study</A>. The "why it works" cell names the specific reason the rewrite describes the right buyer and not the wrong one.
      </P>
      <P>
        The mistake the gallery is built to prevent: a "lazy" rewrite that just paraphrases the keyword. <Code>best CRM</Code> rewritten as <em>"Looking for the best CRM"</em> reads to the matcher as the same string, just longer. A disciplined rewrite reorganizes the underlying buyer scenario into the 4-component shape. Compare row 1: <Code>best CRM for sales teams</Code> becomes <em>"Marketing leaders comparing CRM platforms for a 50-person team, not implementation help."</em> The original named a category. The rewrite names a buyer at a moment.
      </P>

      <PullQuote>
        The original named a category. The rewrite names a buyer at a moment.
      </PullQuote>

      <P>
        None of the 8 rewrites is missing two or more components, because a hint missing two components is a hint that fails the next section's scorecard. A working hint has SOME persona, SOME intent, SOME scope, and (when adjacent intents are dense) SOME disqualifier.
      </P>

      {/* InlineCTA: free Google Ads to ChatGPT Ads converter */}
      <div style={{ background: TEAL_DIM, border: `1px solid rgba(79,205,196,0.35)`, borderRadius: 12, padding: "22px 24px", margin: "32px 0" }}>
        <div style={{ fontSize: 11, fontFamily: "var(--mono)", color: TEAL, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>Free tool</div>
        <div style={{ fontSize: 18, fontFamily: "var(--display)", fontWeight: 700, color: TEXT, marginBottom: 10, lineHeight: 1.3 }}>Translate your keywords into hints in 60 seconds</div>
        <P>
          Paste 5 of your top Google Ads keywords. Our converter classifies each into one of the 5 Patterns and writes a matching context hint. No signup. <A href="/tools/google-ads-to-chatgpt-ads-converter">Try the free Google Ads to ChatGPT Ads Converter</A>.
        </P>
      </div>

      {/* ─── §4 ─ THE HINT-QUALITY SCORECARD ─────────────────────────── */}
      <H2 id="scorecard">The Hint-Quality Scorecard</H2>
      <ScorecardCard />
      <P>
        <Strong>The Hint-Quality Scorecard is a 5-criterion rubric for evaluating a hint before launch. Score persona, intent, scope, disqualifier, and length on a 0-2 scale, total 10. Use 8 as the launch gate; 5-7 means revise; 4 or below means rewrite from scratch.</Strong>
      </P>
      <P>
        Each criterion catches a different failure mode. A weak persona sends spend at users who will never convert. A weak intent verb misses the comparison, evaluation, or migration moment that signals real buying. A weak scope blurs across adjacent buyer scenarios. A missing disqualifier leaves implementation conversations inside the described moment, which risks spend on unconvertible traffic. A blown length cap drifts across multiple scenarios at once. Each component has a specific job, and the scorecard makes the trade-off visible.
      </P>
      <P>
        The two cards above show the same scoring discipline applied to a strong hint and a weak one. The 9/10 hint names a specific persona, a specific decision verb, multi-dimensional scope, and an explicit disqualifier in 87 characters. The 5/10 hint names a generic persona and a specific intent verb but skips scope and disqualifier. It will serve placements against any conversation that pattern-matches "sales team comparing CRMs," including conversations the campaign cannot afford to convert. The hint is not broken. It is not ready.
      </P>
      <P>
        The scorecard's most common failure mode is the marketer who over-rates their own hint. Confidence bias treats a generic persona as "specific enough" and a one-dimensional scope as "tight enough." The discipline that works is to have a teammate score the hint blind. If a teammate without context scores it 6, it is a 6, not the 8 you scored it. Score honestly or skip the rubric entirely. For 10 worked examples scored against this rubric and grouped across all 5 patterns, see the <A href="#worked-examples">10 fully-scored hint examples</A> below.
      </P>

      {/* ─── §4b ─ 10 WORKED EXAMPLES, SCORED (ported from best-examples) ─ */}
      <H2 id="worked-examples">10 worked examples, scored across all 5 patterns</H2>
      <P>
        <Strong>The 10 hints below cover the 5 patterns at 2 per pattern, every one scored against the rubric above and sourced from a real B2B sales conversation, Reddit thread, or support transcript.</Strong> None were invented. Five score a perfect 10 of 10 because the buyer scenario warranted an explicit disqualifier; the other five score 8 of 10 because the persona scope was tight enough to make the disqualifier unnecessary. Both shapes are launch-ready. Skim the table, then read the cards for the scorecard breakdown and provenance behind each.
      </P>

      <HintExamplesTable />

      <H3>Persona + Intent: Hints #1 and #2</H3>
      <P>
        Persona + Intent is the workhorse: the hint names WHO plus WHAT, the right shape when the buyer is well-defined by role and stage and the action is comparison or evaluation. Hint #1 earns 10 of 10 with an implementation-help disqualifier (the dominant adjacent intent in attribution); Hint #2 omits it and still clears the gate at 8 of 10 because the persona is narrow enough to filter on its own.
      </P>
      {HINTS.filter((h) => h.pattern === "Persona + Intent").map(renderHintBlock)}

      <H3>Question: Hints #3 and #4</H3>
      <P>
        Question hints mirror the WH-question the buyer actively asks. They qualify an earlier-stage audience than Persona + Intent (lower immediate CVR, but they reach buyers who shortlist next month). Both score 8 of 10: the named-tool migration in #3 and the production-pipelines scope in #4 do the filtering the disqualifier would otherwise do.
      </P>
      {HINTS.filter((h) => h.pattern === "Question").map(renderHintBlock)}

      <H3>Topic + Disqualifier: Hints #5 and #6</H3>
      <P>
        Topic + Disqualifier bakes an explicit "not X" into the sentence, the closest a hint gets to a negative keyword, though it describes rather than enforces: OpenAI says hints cannot enforce exclusions. Both hints score a perfect 10 of 10, which tracks the pattern's reliance on the disqualifier component. Note how the disqualifier is woven in as a clause, not bolted on as a tag, so it reads as part of one description.
      </P>
      {HINTS.filter((h) => h.pattern === "Topic + Disqualifier").map(renderHintBlock)}

      <H3>Outcome: Hints #7 and #8</H3>
      <P>
        Outcome hints describe the result the buyer wants and reach them weeks before they name a category, the pattern's structural advantage for high-consideration B2B. Hint #7 lands 10 of 10 with an enterprise-deals disqualifier; Hint #8 uses an integration anchor (HubSpot) to do the qualifying work without one.
      </P>
      {HINTS.filter((h) => h.pattern === "Outcome").map(renderHintBlock)}

      <H3>Stack Comparison: Hints #9 and #10</H3>
      <P>
        Stack Comparison is the displacement pattern: the named-tool anchor is the matcher's high-precision signal. Hint #9 earns 10 of 10 with a company-size disqualifier; Hint #10 omits it because the budget anchor ($50K monthly Datadog spend) is itself the qualifying scope.
      </P>
      {HINTS.filter((h) => h.pattern === "Stack Comparison").map(renderHintBlock)}

      <H3>What good has in common across all 10</H3>
      <P>
        Persona, intent, scope, and length hit 100% across the 10; the disqualifier hits 50%. Four universal components, one discretionary. The average hint runs 108 characters, well under the 280 drift threshold, and every hint names a specific decision verb ("comparing," "migrating," "trying to cut") rather than a generic one. The decision verb is what the matcher embeds against active conversations.
      </P>
      <HintQualityFrequency />
      <P>
        These 10 are templates for study and modification, not a launch-ready list to copy. The campaigns that compound revenue come from hints sourced inside your own sales conversations, written in your own buyer's language, and scored against the same rubric. The deeper reason to pair each hint with a content brief on the same scenario is the <A href="/glossary/ai-visibility-lift">AI Visibility Lift</A>, covered in the AEO bridge below.
      </P>

      {/* ─── §5 ─ 5 HINT-KILLERS ─────────────────────────────────────── */}
      <H2 id="hint-killers">5 hint-killers to avoid</H2>
      <P>
        <Strong>5 process pitfalls compound between sales conversation and launched ad group, each one distinct from <A href="/blog/what-are-context-hints-in-chatgpt-ads#what-context-hints-are-not">Part 1's 5 conceptual mistakes</A>.</Strong> The conceptual mistakes are about what a hint IS. The process pitfalls below are about what gets done wrong between the closed-won note and the live campaign.
      </P>

      <H3>1. Skipping sales sourcing</H3>
      <P>
        The most common failure is starting with a keyword spreadsheet instead of a 60-minute sales conversation. Keyword tools surface what users TYPE; sales conversations surface what users WANT. The matcher matches conversations, not queries, so the source artifact has to be a buyer scenario, not a search-volume table. Marketers who keep their old keyword tooling in the loop tend to ship hints that read like reformatted keyword strings. The matcher reads them back as the same string, just longer.
      </P>

      <H3>2. Stacking multiple hints in one ad group</H3>
      <P>
        Google Ads rewards breadth: more keywords means more coverage. ChatGPT Ads punishes it: more hints in one ad group means embedding collision and lower match precision. The discipline inverts. One hint per ad group, multiple ad groups for multiple scenarios. The instinct to consolidate three similar hints into one ad group "to keep the campaign tidy" is almost always the wrong instinct in the new platform.
      </P>

      <H3>3. Translating Google Ads negatives as separate hints</H3>
      <P>
        A Google Ads negative keyword sheet does not translate into a separate "negative hint" in ChatGPT Ads, because the platform has no negative-hint field. The disqualifier component of the 4-part hint is the closest substitute, though it describes rather than enforces: OpenAI says hints cannot enforce exclusions. Bake the disqualifier into the hint itself ("not implementation help"), not into a parallel field that does not exist. Marketers who try to recreate negative-keyword infrastructure tend to over-build for a paradigm the platform retired.
      </P>

      <H3>4. Treating the scorecard as a retrospective tool</H3>
      <P>
        Some marketers run campaigns first, then score the hints after seeing performance. That inverts the rubric's purpose. The scorecard catches drift cheaply before spend. After launch, the matcher has already done that job (poorly, because the drift was there to begin with), and the only path forward is rewriting and relaunching, which costs another two-week cohort. Score before launch, not after.
      </P>

      <H3>5. Iterating hints inside the two-week cohort</H3>
      <P>
        Embedding-matched targeting needs density to produce readable signal. Changing a hint after 3 days of data is changing the target before the matcher has converged on it. Run two weeks per cohort. Let the matcher converge. Then iterate between cohorts, not within them. Marketers who optimize too aggressively early tend to end the first 30 days with a stack of unfinished tests and no signal, instead of a stack of completed cohorts and a clear iteration direction.
      </P>

      {/* ─── §6 ─ AEO BRIDGE ─────────────────────────────────────────── */}
      <H2 id="aeo-bridge">How hint research feeds your organic AEO strategy</H2>
      <P>
        <Strong>The 8 buyer scenarios you sourced from sales for paid hint targeting are the same 8 scenarios that should drive your organic <A href="/glossary/answer-engine-optimization">Answer Engine Optimization</A> content strategy.</Strong> One research artifact, two channel applications. Paid hints buy immediate placement; organic AEO content earns long-term citations across ChatGPT, Perplexity, and Microsoft Copilot. The two channels reinforce each other when worked from the same scenario list.
      </P>

      <PullQuote>
        Run paid hints and organic AEO as one project, off the same buyer scenario list.
      </PullQuote>

      <P>
        The mechanic is the <A href="/glossary/ai-visibility-lift">AI Visibility Lift</A>: the compounding effect of paid placements, organic citations, and branded recall on the same buyer scenarios across the same set of conversations. Paid placements train brand recognition. Organic citations earn the unpaid mention next to the paid placement. Branded recall makes the next conversation pre-converted. The three layers measure differently but move together when targeted at the same scenarios.
      </P>

      <P>
        For each scenario in your list, the question doubles. Who is the buyer at this moment, and what page or post or schema markup do we already have (or need to ship) that an AI engine would cite as the canonical reference? If the answer is "nothing yet," the scenario is a paid-only opportunity until the organic asset ships. If the answer is "a glossary entry" or "a how-to post," the scenario is already a paid plus organic compound. The Scorecard above evaluates the paid hint; the parallel question (does an organic asset exist for the same scenario) determines whether the campaign is renting visibility or compounding it.
      </P>

      <P>
        The pattern that wins through 2026 is hint research that doubles as content research. Every scenario the team finds in sales is a paid opportunity now and an organic opportunity for the next 6 to 18 months. Teams that source hint scenarios but do not reuse them for content miss half the lift. Teams that publish organic content without sourcing scenarios from sales miss the precision that makes the content cite-able.
      </P>

      <P>
        The two channels are not separate work streams. They are two views of the same buyer scenario list, and the integrated view is what our <A href="/platform/ai-ads">AI Ads platform</A> is built around.
      </P>

      {/* ─── §7 ─ FAQ ────────────────────────────────────────────────── */}
      <H2 id="faq">Frequently Asked Questions</H2>
      <FAQItems faqs={faqs} />

      {/* ─── §8 ─ RELATED READING ────────────────────────────────────── */}
      <H2 id="related">Related Reading</H2>
      <ul style={{ paddingLeft: 20, margin: "0 0 18px" }}>
        <Li><A href="/blog/what-are-context-hints-in-chatgpt-ads">What Are Context Hints in ChatGPT Ads? The 5 Patterns Defined</A></Li>
        <Li><A href="/blog/how-to-convert-google-ads-to-chatgpt-ads">How to Convert Google Ads to ChatGPT Ads: A 2026 Migration Methodology</A></Li>
        <Li><A href="/blog/how-to-set-up-chatgpt-ad-campaign">How to Set Up a ChatGPT Ad Campaign in 8 Steps</A></Li>
        <Li><A href="/blog/how-chatgpt-ads-work">How Do ChatGPT Ads Work? Format, Context Hints, and the AI Visibility Lift (October 2026 Update)</A></Li>
      </ul>

      {/* ─── Schemas ─────────────────────────────────────────────────── */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(examplesItemListSchema) }} />
    </article>
  );
}

const post = {
  slug: "how-to-write-effective-context-hints",
  title: "How to Write Effective Context Hints for ChatGPT Ads",
  seoTitle: "How to Write Effective Context Hints for ChatGPT Ads",
  excerpt: "Effective context hints name a buyer at a moment, not a search string. The 5-Step Hint Method, the Hint-Quality Scorecard, and 8 worked rewrites.",
  metaDescription: "How to write context hints for ChatGPT Ads campaigns: 5-step method, 5-question quality scorecard, 8 keyword-to-hint rewrites, and 5 hint-killers to avoid.",
  date: "2026-05-08",
  lastUpdated: "2026-10-17",
  readTime: "14 min read",
  category: "AI Ads",
  fiveA: "ads",
  tags: ["context hints", "ChatGPT Ads", "AI advertising", "ad targeting", "5-Step Hint Method"],
  faqs,
  sidebarCTA: "converter",
  Content,
};

export default post;
